import { Moment } from 'moment'

import {
  UpdateThingToDoItemViewModel,
  UpdateThingToDoItemRequest,
  UpdateRestaurantBarItemViewModel,
  UpdateRestaurantBarItemRequest,
  UpdateCarRentalItemRequest,
  UpdateCarRentalItemViewModel,
  UpdateOtherTransportItemViewModel,
  UpdateOtherTransportItemRequest,
  UpdateFerryItemViewModel,
  UpdateFerryItemRequest,
  UpdateTrainItemViewModel,
  UpdateTrainItemRequest,
  UpdateBusItemRequest,
  UpdateBusItemViewModel,
  UpdateNoteItemRequest,
  UpdateNoteItemViewModel,
  UpdateEventItemViewModel,
  UpdateEventItemRequest,
  UpdateAttractionItemViewModel,
  UpdateAttractionItemRequest,
  UpdateCruiseItemViewModel,
  UpdateCruiseItemRequest,
  UpdateTourItemViewModel,
  UpdateTourItemRequest,
  UpdateHotelItemRequest,
  UpdateHotelItemViewModel,
  UpdateFlightItemViewModel,
  UpdateFlightItemRequest,
  UpdateAirBnbItemRequest,
  UpdateAirBnbItemViewModel,
} from './models'

import {
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
} from 'constants/dateFormats'
import { Place } from 'tripPlanner/types/common'
import { TripItem } from 'tripPlanner/types/tripItem'

export function extractEditableStartDateTimeStrings<
  I extends {
    startDate: Moment | undefined
    startTime: Moment | undefined
  },
>(item: I): { startTime: string | undefined; startDate: string | undefined } {
  const dateTimeStrings: {
    startTime: string | undefined
    startDate: string | undefined
  } = {
    startTime: undefined,
    startDate: undefined,
  }

  if (item.startTime) {
    dateTimeStrings.startTime = item.startTime.format(SHORT_TIME_FORMAT_24_HOUR)
  }

  if (item.startDate) {
    dateTimeStrings.startDate = item.startDate.format(ISO_DATE_FORMAT)
  }

  return dateTimeStrings
}

export function extractEditableEndDateTimeStrings<
  I extends {
    endDate: Moment | undefined
    endTime: Moment | undefined
  },
>(item: I): { endTime: string | undefined; endDate: string | undefined } {
  const dateTimeStrings: {
    endTime: string | undefined
    endDate: string | undefined
  } = {
    endTime: undefined,
    endDate: undefined,
  }

  if (item.endTime) {
    dateTimeStrings.endTime = item.endTime.format(SHORT_TIME_FORMAT_24_HOUR)
  }

  if (item.endDate) {
    dateTimeStrings.endDate = item.endDate.format(ISO_DATE_FORMAT)
  }

  return dateTimeStrings
}

export function isEndDateSame(
  item: Extract<TripItem, { endDate: Moment | undefined }>,
) {
  return (
    !!item.startDate &&
    !!item.endDate &&
    item.startDate.toISOString() === item.endDate.toISOString()
  )
}

export function isEndPlaceSame(item: Extract<TripItem, { endPlace: Place }>) {
  return (
    'id' in item.startPlace &&
    'id' in item.endPlace &&
    item.startPlace.id === item.endPlace.id
  )
}

export const updateFlightPayloadFromViewModel = (
  viewModel: UpdateFlightItemViewModel,
): UpdateFlightItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateAirbnbPayloadFromViewModel = (
  viewModel: UpdateAirBnbItemViewModel,
): UpdateAirBnbItemRequest => {
  const {
    place,
    startDate,
    startTime,
    endDate,
    endTime,
    type,
    code,
    confirmationCode,
    confirmationItemCodes,
    confirmationUrl,
    host,
    notes,
    phone,
    price,
    sourceType,
    isBooked,
    url,
  } = viewModel

  return {
    place,
    startDate,
    startTime,
    endDate,
    endTime,
    type,
    code,
    confirmationCode,
    confirmationItemCodes,
    confirmationUrl,
    host,
    notes,
    phone,
    price,
    sourceType,
    isBooked,
    url,
  }
}

export const updateHotelPayloadFromViewModel = (
  viewModel: UpdateHotelItemViewModel,
): UpdateHotelItemRequest => {
  const {
    place,
    startDate,
    startTime,
    endDate,
    endTime,
    type,
    code,
    confirmationCode,
    confirmationItemCodes,
    confirmationUrl,
    notes,
    phone,
    price,
    sourceType,
    isBooked,
    url,
  } = viewModel

  return {
    place,
    startDate,
    startTime,
    endDate,
    endTime,
    type,
    code,
    confirmationCode,
    confirmationItemCodes,
    confirmationUrl,
    notes,
    phone,
    price,
    sourceType,
    isBooked,
    url,
  }
}

export const updateTourPayloadFromViewModel = (
  viewModel: UpdateTourItemViewModel,
): UpdateTourItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return rest
}

export const updateCruisePayloadFromViewModel = (
  viewModel: UpdateCruiseItemViewModel,
): UpdateCruiseItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return rest
}

export const updateAttractionPayloadFromViewModel = (
  viewModel: UpdateAttractionItemViewModel,
): UpdateAttractionItemRequest => {
  const { ...rest } = viewModel

  return rest
}

export const updateEventPayloadFromViewModel = (
  viewModel: UpdateEventItemViewModel,
): UpdateEventItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateThingToDoPayloadFromViewModel = (
  viewModel: UpdateThingToDoItemViewModel,
): UpdateThingToDoItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return rest
}

export const updateNotePayloadFromViewModel = (
  viewModel: UpdateNoteItemViewModel,
): UpdateNoteItemRequest => {
  const { ...rest } = viewModel

  return rest
}

export const updateBusPayloadFromViewModel = (
  viewModel: UpdateBusItemViewModel,
): UpdateBusItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateTrainPayloadFromViewModel = (
  viewModel: UpdateTrainItemViewModel,
): UpdateTrainItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateFerryPayloadFromViewModel = (
  viewModel: UpdateFerryItemViewModel,
): UpdateFerryItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateOtherTransportPayloadFromViewModel = (
  viewModel: UpdateOtherTransportItemViewModel,
): UpdateOtherTransportItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateCarRentalPayloadFromViewModel = (
  viewModel: UpdateCarRentalItemViewModel,
): UpdateCarRentalItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return rest
}

export const updateRestaurantBarPayloadFromViewModel = (
  viewModel: UpdateRestaurantBarItemViewModel,
): UpdateRestaurantBarItemRequest => {
  const { ...rest } = viewModel

  return rest
}
